/**
 * Mirrored SUI component base class strings for Radix raw-primitive bypasses.
 *
 * WHY THESE EXIST
 * The SUI tooltip/popover/dropdown-menu *Content wrappers hardcode their Radix
 * Portal to `document.body` and don't expose a `container` prop.  In fullscreen
 * mode the Fullscreen API only renders the fullscreen element and its
 * descendants, so anything portalled to body is invisible.
 *
 * TEMPORARY SOLUTION
 * We use raw Radix primitives (`Radix<X>.Portal container={...}`) and reproduce
 * the SUI wrapper's class string here as a named constant.  A single constant
 * per component type means SUI drift is caught in one place rather than spread
 * across multiple feature files.
 *
 * REAL FIX
 * Upstream ticket: add a `container` prop to SUI TooltipContent / PopoverContent
 * / DropdownMenuContent so these bypass files can be deleted.
 */

/** Matches SUI `PopoverContent` base output (no per-instance overrides). */
export const RADIX_POPOVER_CONTENT_BASE =
  'bg-bg-surface-overlay text-text-neutral-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 origin-(--radix-popover-content-transform-origin) rounded-md border border-border-neutral shadow-md outline-hidden';

/** Matches SUI `DropdownMenuContent` base output (no per-instance overrides). */
export const RADIX_DROPDOWN_MENU_CONTENT_BASE =
  'bg-bg-surface-overlay text-text-neutral-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border border-border-neutral p-1 shadow-md';

/**
 * Matches the SUI `TooltipContent` animation/positioning subset.
 *
 * NOTE: `text-balance` from the standard SUI TooltipContent is intentionally
 * omitted — it's meant for short headline-style text and this is a multi-line
 * rich tooltip. `text-xs` is NOT omitted: most inner elements (description,
 * field labels, dates) have no explicit size override and rely on this base
 * for their 12px sizing, same as the original SUI default.
 */
export const RADIX_TOOLTIP_CONTENT_BASE =
  'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 origin-(--radix-tooltip-content-transform-origin) rounded-md text-xs';
